import FileInputHandler from "./file-input-handler";
import FileProcessor from "./file-processor";
import DialogController from "./dialog-controller";

const DEFAULT_FACING_MODE = "environment";
const VALID_FACING_MODES = new Set(["user", "environment"]);

export default class CameraUploader {
  #buttonSelector;

  #onImage;

  #onError;

  #isMultiple;

  #facingMode;

  #destroyed = false;

  #fileInputHandler;

  #fileProcessor;

  #dialogController;

  #permissionDeniedCount = 0;

  #maxPermissionRetries = 1;

  // Getters
  get #$button() {
    return document.querySelector(this.#buttonSelector);
  }

  get $button() {
    return this.#$button;
  }

  get isLocked() {
    return this.#dialogController.isLocked;
  }

  constructor({
    button,
    multiple = false,
    facingMode = DEFAULT_FACING_MODE,
    onImage,
    onError,
    legacyAccept = false,
    maxPermissionRetries = 1
  } = {}) {
    if (!button) throw new Error("Button selector is required");

    this.#buttonSelector = button;

    if (!this.#$button) {
      throw new Error(`Button not found: ${button}`);
    }

    this.#onImage = onImage;
    this.#onError = onError;
    this.#isMultiple = Boolean(multiple);
    this.#facingMode = VALID_FACING_MODES.has(facingMode) ? facingMode : "";
    this.#maxPermissionRetries = Math.max(0, maxPermissionRetries);

    this.init(legacyAccept);
  }

  // Inits
  init(legacyAccept) {
    this.#fileInputHandler = new FileInputHandler(this.#isMultiple, this.#facingMode);
    this.#fileProcessor = new FileProcessor();
    this.#dialogController = new DialogController();

    this.#fileInputHandler.createFileInput(legacyAccept);

    // Bind event handlers to this instance
    const cameraUploader = this;
    const boundHandlers = {
      handleClick: this.handleClick.bind(this),
      handleChange: this.handleChange.bind(this),
      handleBlur: this.handleBlur.bind(this),
      handleFocus: this.handleFocus.bind(this),
      handleVisibilityChange: this.handleVisibilityChange.bind(this),
      unlock: this.unlock.bind(this),
      $button: this.$button,
      get isLocked() {
        return cameraUploader.isLocked;
      },
      addTimer: this.addTimer.bind(this)
    };

    this.#fileInputHandler.attachEventListeners(boundHandlers);
  }

  // Actions
  resetInput() {
    this.#fileInputHandler.resetInput();
  }

  getFiles() {
    return this.#fileInputHandler.getFiles();
  }

  setFacingMode(mode) {
    this.#facingMode = VALID_FACING_MODES.has(mode) ? mode : "";
    this.#fileInputHandler.setFacingMode(this.#facingMode);
  }

  setMultiple(flag) {
    this.#isMultiple = Boolean(flag);
    this.#fileInputHandler.setMultiple(this.#isMultiple);
  }

  destroy() {
    if (this.#destroyed) return;

    this.#dialogController.destroy();
    this.#fileInputHandler.destroy();

    this.#onImage = null;
    this.#onError = null;
    this.#destroyed = true;
  }

  // Event handlers
  handleClick(event) {
    if (!this.#dialogController.canHandleClick()) return;

    this.#dialogController.lockInput();
    this.#dialogController.setupUnlockFallback();

    if (CameraUploader.#isUserInitiated(event)) {
      this.#handleUserInitiatedClick();
    }
  }

  handleChange() {
    this.#dialogController.unlock();

    const files = this.getFiles();
    if (!files.length) {
      this.#handlePermissionDenied();
      return;
    }

    this.#permissionDeniedCount = 0;

    const payload = this.#fileProcessor.processFiles(files, this.#onError);
    if (payload && this.#onImage) {
      this.#onImage(this.#isMultiple ? payload : payload[0]);
    }
  }

  handleBlur() {
    this.#dialogController.handleBlur();
  }

  handleFocus() {
    this.#dialogController.handleFocus();
  }

  handleVisibilityChange() {
    this.#dialogController.handleVisibilityChange();
  }

  unlock() {
    this.#dialogController.unlock();
  }

  addTimer(timer) {
    this.#dialogController.addTimer(timer);
  }

  // Actions
  #handleUserInitiatedClick() {
    try {
      this.#fileInputHandler.$input.addEventListener("focus", this.#onFileDialogFocus.bind(this), {
        once: true
      });
      this.#fileInputHandler.$input.click();
    } catch (error) {
      this.#handleOpenError("open_failed", error);
    }
  }

  #onFileDialogFocus() {
    this.#fileInputHandler.$input.addEventListener("cancel", this.#onFileDialogCancel.bind(this), {
      once: true
    });
  }

  #onFileDialogCancel() {
    this.#dialogController.unlock();
    this.#handlePermissionDenied();
  }

  #handleOpenError(errorType, error) {
    this.#dialogController.unlock();
    this.#reportError(errorType, error);
  }

  #reportError(code, messageOrError) {
    if (!this.#onError) return;

    const error =
      typeof messageOrError === "string"
        ? { code, message: messageOrError }
        : { code, error: messageOrError };

    this.#onError(error);
  }

  #handlePermissionDenied() {
    this.#permissionDeniedCount += 1;

    this.#dialogController.unlock();

    if (this.#permissionDeniedCount <= this.#maxPermissionRetries) {
      this.resetInput();

      this.#reportError("camera_permission_denied", {
        message: "Camera permission was denied. Please allow camera access to take photos.",
        retryCount: this.#permissionDeniedCount,
        maxRetries: this.#maxPermissionRetries
      });
    } else {
      this.#reportError("camera_permission_permanently_denied", {
        message:
          "Camera access is required to take photos. Please enable camera permissions in your device settings and try again.",
        retryCount: this.#permissionDeniedCount
      });
    }
  }

  static #isUserInitiated(event) {
    return event && event.isTrusted;
  }
}
